import {
  API_URL,
  REFRESHTOKEN,
  SESSIONMGR_URL // SPJWT,
  // SPTENANTID,
  // TOKEN_EXPIRY
} from "@/configs";
import { attemptSessionRefresh } from "@/lib/auth/sessionRefresh";
import { logger } from "@/lib/logger";
import { logOutUser } from "@/slices/auth/authThunk";
import { store } from "@/store";
import { removeCookie } from "@/utils/session";

import type { ApiError, ApiResult } from "./apiResult";
import authHeader, { getHeaders } from "./authHeader";
import { joinServiceUrl } from "./servicePaths";

// header request
const HEADERS = {
  Accept: "application/json",
  "Content-Type": "application/json"
};

const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;

function stripQueryFromUrl(url: string | undefined): string | undefined {
  if (!url || typeof url !== "string") return url;

  try {
    const parsed = new URL(url, typeof window !== "undefined" ? window.location.origin : "http://localhost");
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return url.split("?")[0];
  }
}

function isAbortError(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }

  const maybeError = error as { name?: string; code?: number; message?: string };
  return (
    maybeError.name === "AbortError" ||
    maybeError.code === 20 ||
    maybeError.message === "AbortError" ||
    maybeError.message === "The operation was aborted."
  );
}

// test api
// mock: mock api 1, mock2: mock api 2, custom: api unknown
//
const API_TYPE = {
  apiurl: "apiurl",
  apiChatUrl: "apiChatUrl",
  mock: "mock",
  mock2: "mock2",
  custom: "custom"
};
export interface FetchRawOptions {
  type: keyof typeof API_TYPE;
  path: string;
  method: string;
  headers?: Record<string, string>;
  body?: any; // Consider using unknown or a generic type if you want stricter typing
  params?: Record<string, any>;
  withToken?: boolean | string;
  serverSideReq?: any; // If you know the shape, specify it
  responseType?: "json" | "text" | "blob" | "raw"; // NEW: specify response type; 'raw' returns the raw fetch Response for streaming
  signal?: AbortSignal;
  timeoutMs?: number;
  suppressAuthLogoutOn401?: boolean;
  suppressAuthLogoutOn401Codes?: readonly string[];
  suppressAuthLogoutAfterRefresh401?: boolean;
  suppressNoTenantLogoutAndRedirect?: boolean;
  suppressNetworkErrorLogs?: boolean;
}
// {
//   type: keyof typeof API_TYPE;
//   path: string;
//   method: string;
//   headers?: any;
//   body?: any;
//   params?: any;
//   withToken?: boolean | string;
//   serverSideReq?: any;
// }
export interface FetchRawResponse {
  status: number;
  headers: Record<string, any>;
  content: any;
}

const SESSION_INVALIDATING_FORBIDDEN_CODES = new Set([
  "NO_TENANT",
  "account_inactive",
  "workspace_inactive",
  "workspace_membership_inactive",
  "workspace_membership_missing",
  "workspace_not_found",
  "workspace_resolution_failed",
  "workspace_user_claim_mismatch"
]);

function responseErrorCode(content: unknown): string | undefined {
  if (!content || typeof content !== "object" || Array.isArray(content)) return undefined;
  const record = content as { code?: unknown; error?: { code?: unknown } };
  if (typeof record.code === "string") return record.code;
  return typeof record.error?.code === "string" ? record.error.code : undefined;
}

function shouldSuppressAuthLogout(
  httpResponseCode: number,
  content: unknown,
  suppressAuthLogoutOn401: boolean,
  suppressAuthLogoutOn401Codes?: readonly string[]
): boolean {
  if (suppressAuthLogoutOn401) return true;
  if (httpResponseCode !== 401) return false;
  const code = responseErrorCode(content);
  return code !== undefined && suppressAuthLogoutOn401Codes?.includes(code) === true;
}

export function isSessionInvalidatingForbidden(content: unknown): boolean {
  if (!content || typeof content !== "object") {
    return false;
  }

  const code = (content as { code?: unknown }).code;
  return typeof code === "string" && SESSION_INVALIDATING_FORBIDDEN_CODES.has(code);
}

export default function FETCHRAW({
  type,
  path,
  method,
  headers = {},
  body,
  params = {},
  withToken = true,
  serverSideReq,
  responseType = "json",
  signal,
  timeoutMs,
  suppressAuthLogoutOn401 = false,
  suppressAuthLogoutOn401Codes,
  suppressAuthLogoutAfterRefresh401 = false,
  suppressNoTenantLogoutAndRedirect = false,
  suppressNetworkErrorLogs = false
}: FetchRawOptions): Promise<FetchRawResponse> {
  return new Promise(function (resolve, reject) {
    startFetchRaw(
      type,
      path,
      method,
      headers,
      body,
      params,
      withToken,
      serverSideReq,
      responseType,
      signal,
      false,
      suppressAuthLogoutOn401,
      suppressAuthLogoutOn401Codes,
      suppressAuthLogoutAfterRefresh401,
      suppressNoTenantLogoutAndRedirect,
      timeoutMs,
      suppressNetworkErrorLogs
    )
      .then((response) => {
        resolve(response);
      })
      .catch((error) => reject(error));
  });
}

function startFetchRaw(
  type,
  path,
  method,
  headers,
  body,
  params,
  withToken,
  serverSideReq,
  responseType = "json",
  signal?: AbortSignal,
  didRefreshRetry = false,
  suppressAuthLogoutOn401 = false,
  suppressAuthLogoutOn401Codes?: readonly string[],
  suppressAuthLogoutAfterRefresh401 = false,
  suppressNoTenantLogoutAndRedirect = false,
  timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
  suppressNetworkErrorLogs = false
): Promise<FetchRawResponse> {
  return new Promise((resolve, reject) => {
    const requestTimeoutMs = Number.isFinite(timeoutMs) && timeoutMs > 0 ? timeoutMs : DEFAULT_REQUEST_TIMEOUT_MS;
    const requestController = typeof AbortController !== "undefined" ? new AbortController() : undefined;
    let timedOut = false;
    const abortRequest = () => requestController?.abort();
    const removeAbortListener = () => signal?.removeEventListener("abort", abortRequest);
    if (signal) {
      if (signal.aborted) abortRequest();
      else signal.addEventListener("abort", abortRequest, { once: true });
    }
    const timeout = setTimeout(() => {
      timedOut = true;
      requestController?.abort();
      removeAbortListener();
      reject({
        status: 0,
        headers: {},
        content: null,
        message: "requestApiTimeout",
        localizeMessage: "requestApiTimeout"
      });
    }, requestTimeoutMs);
    let apiString;
    switch (type) {
      case API_TYPE.apiurl:
        apiString = API_URL;
        break;
      case API_TYPE.apiChatUrl:
        apiString = SESSIONMGR_URL;
        break;
      // case API_TYPE.mock: // mock api type
      //   apiString = API_URL_MOCK;
      //   headersApi["origin"] = API_URL_MOCK;
      //   break;
      // case API_TYPE.mock2: // mock api type
      //   apiString = API_URL_MOCK_2;
      //   headersApi["origin"] = API_URL_MOCK_2;
      //   break;
      case API_TYPE.custom:
        apiString = "";
        break;
    }

    if (typeof path !== "string" || path.trim() === "") {
      const stack = new Error("FETCHRAW invalid path").stack;
      logger.error("FETCHRAW invalid path", { type, path, method, stack });
      clearTimeout(timeout);
      removeAbortListener();
      return reject({
        status: 0,
        headers: {},
        content: null,
        message: "invalidPath",
        localizeMessage: "invalidPath"
      });
    }

    if (
      (type === API_TYPE.apiurl || type === API_TYPE.apiChatUrl) &&
      (typeof apiString !== "string" || apiString.trim() === "")
    ) {
      const stack = new Error("FETCHRAW missing base url").stack;
      logger.error("FETCHRAW missing base url", { type, apiString, method, path, stack });
      clearTimeout(timeout);
      removeAbortListener();
      return reject({
        status: 0,
        headers: {},
        content: null,
        message: "missingApiBaseUrl",
        localizeMessage: "missingApiBaseUrl"
      });
    }

    // Normalize query params: drop null/undefined values to avoid sending
    // e.g. "subject_id=undefined" to the backend. Log when we do this so
    // callers passing undefined/null can be tracked and fixed at the source.
    let paramsString = "";
    const paramsKeys = Object.keys(params || {});
    const filteredParams: Record<string, any> = {};
    const droppedKeys: string[] = [];

    paramsKeys.forEach((key) => {
      const value = params[key];
      if (value === undefined || value === null) {
        droppedKeys.push(key);
        return;
      }
      filteredParams[key] = value;
    });

    if (droppedKeys.length > 0) {
      logger.warn("FETCHRAW dropping null/undefined query params", {
        path,
        method,
        droppedKeys
      });
    }

    const filteredKeys = Object.keys(filteredParams);

    filteredKeys.forEach((key, index) => {
      const rawValue = filteredParams[key];
      const trimmedValue = typeof rawValue === "string" ? rawValue.trim() : rawValue;
      const encodedValue =
        typeof trimmedValue === "object"
          ? encodeURIComponent(JSON.stringify(trimmedValue))
          : encodeURIComponent(trimmedValue?.toString());
      paramsString += (index === 0 ? "?" : "&") + key + "=" + encodedValue;
    });

    const fetchURL = joinServiceUrl(apiString, `${path}${paramsString}`);
    const bodyrequest = body || undefined;
    let httpResponseCode = 200; // may be better to set response
    let headerResponse = {};
    async function getAuthHeader(headersRequest, serverSideReq, requestBaseUrl) {
      if (headersRequest && headersRequest.Authorization) {
        return {}; // skip getAuthHeader if request already has it. e.g. getRefreshToken
      }
      return await authHeader(withToken, serverSideReq, requestBaseUrl);
    }
    const isFormData = body instanceof FormData;
    const isBlob = body instanceof Blob;

    // If callers pass a plain object while using JSON content-type,
    // fetch() will coerce it to "[object Object]" unless we stringify.
    const normalizeBody = () => {
      if (method.toLowerCase() === "get") return undefined;
      if (bodyrequest === undefined || bodyrequest === null) return bodyrequest;
      if (isFormData || isBlob) return bodyrequest;

      const contentType = String(
        headers?.["Content-Type"] || headers?.["content-type"] || HEADERS["Content-Type"] || ""
      );
      const expectsJson = contentType.toLowerCase().includes("application/json");
      if (!expectsJson) return bodyrequest;

      if (typeof bodyrequest === "string") return bodyrequest;
      return JSON.stringify(bodyrequest);
    };

    const normalizedBody = normalizeBody();

    return getAuthHeader(headers, serverSideReq, apiString)
      .then((authHeaderValue) =>
        fetch(fetchURL, {
          credentials: "include",
          method,
          headers: {
            ...(!isFormData && !isBlob && HEADERS),
            ...(isBlob && { "Content-Type": body.type }),
            ...headers,
            ...authHeaderValue
          },
          body: normalizedBody,
          signal: requestController?.signal ?? signal
        })
      )
      .then((response) => {
        if (!response) {
          return false;
        }

        const { status } = response;
        headerResponse = getHeaders(response);
        httpResponseCode = status;

        if (httpResponseCode === 204) {
          // 204 means No Content, so we don't need to parse JSON
          return { content: null };
        }
        if (httpResponseCode === 401 && response && response.url.endsWith("/session/refresh")) {
          removeCookie(REFRESHTOKEN); // DO NOT REMOVE...  not best option but will handle expired refreshtoken
        }

        // Handle responseType
        // NOTE: If the backend returns JSON but responseType is "blob", response.blob() will be empty.
        // Only use "blob" if you know the backend is streaming a file.
        if (responseType === "raw") {
          clearTimeout(timeout);
          removeAbortListener();
          // Caller intends to stream the response body. Do not read or parse the body here.
          // Return the raw Response object as content so callers can use response.body.getReader().
          return Promise.resolve({ content: response });
        }

        if (responseType === "blob") {
          // If Content-Type is application/json, parse as JSON instead of blob
          const contentType = response.headers.get("Content-Type") || "";
          if (contentType.includes("application/json")) {
            return response.json().then((json) => ({ content: json }));
          }
          return response.blob().then((blob) => ({ content: blob }));
        } else if (responseType === "text") {
          return response.text().then((text) => ({ content: text }));
        } else {
          // Default: try to parse as JSON, fallback to text if fails
          const responseClone = response.clone();
          return response.json().catch(async () => {
            const text = await responseClone.text();
            return { content: text };
          });
        }
      })
      .then((responseJson) => {
        clearTimeout(timeout);
        removeAbortListener();
        // Unwrap blob if responseType is "blob"
        let finalContent = responseJson;
        if (responseType === "blob" && responseJson && responseJson.content instanceof Blob) {
          finalContent = responseJson.content;
        }

        const isRefreshEndpoint401 = httpResponseCode === 401 && fetchURL.endsWith("/session/refresh");
        const isAuthRequest = withToken !== false && withToken !== "false";
        const suppressAuthLogoutForResponse = shouldSuppressAuthLogout(
          httpResponseCode,
          finalContent,
          suppressAuthLogoutOn401,
          suppressAuthLogoutOn401Codes
        );
        const shouldTryRefreshAndRetry401 =
          httpResponseCode === 401 &&
          isAuthRequest &&
          typeof window !== "undefined" &&
          !serverSideReq &&
          !didRefreshRetry &&
          !suppressAuthLogoutForResponse &&
          !isRefreshEndpoint401;

        if (shouldTryRefreshAndRetry401) {
          void attemptSessionRefresh().then((refreshed) => {
            if (refreshed) {
              startFetchRaw(
                type,
                path,
                method,
                headers,
                body,
                params,
                withToken,
                serverSideReq,
                responseType,
                signal,
                true,
                suppressAuthLogoutOn401,
                suppressAuthLogoutOn401Codes,
                suppressAuthLogoutAfterRefresh401,
                suppressNoTenantLogoutAndRedirect,
                timeoutMs,
                suppressNetworkErrorLogs
              )
                .then(resolve)
                .catch(reject);
              return;
            }

            handleResponse({
              responseJson: {
                status: httpResponseCode,
                headers: headerResponse,
                content: finalContent,
                url: fetchURL
              },
              httpResponseCode,
              requestMeta: { method, path },
              onFullfill: resolve,
              onReject: reject,
              suppressAuthLogoutOn401: suppressAuthLogoutOn401 || suppressAuthLogoutAfterRefresh401,
              suppressAuthLogoutOn401Codes,
              suppressNoTenantLogoutAndRedirect
            });
          });
          return;
        }

        handleResponse({
          responseJson: {
            status: httpResponseCode,
            headers: headerResponse,
            content: finalContent,
            url: fetchURL
          },
          httpResponseCode,
          requestMeta: { method, path },
          onFullfill: resolve,
          onReject: reject,
          suppressAuthLogoutOn401: suppressAuthLogoutOn401 || suppressAuthLogoutAfterRefresh401,
          suppressAuthLogoutOn401Codes,
          suppressNoTenantLogoutAndRedirect
        });
      })
      .catch((error) => {
        clearTimeout(timeout);
        removeAbortListener();

        if (timedOut) {
          return reject({
            status: 0,
            headers: {},
            content: null,
            message: "requestApiTimeout",
            localizeMessage: "requestApiTimeout"
          });
        }

        if (isAbortError(error)) {
          return reject(error);
        }

        // Detect network-level failures (backend unreachable) by checking for
        // TypeError from fetch() itself (e.g. "Failed to fetch").
        const isNetworkError =
          error instanceof TypeError ||
          (error?.message &&
            (error.message.includes("Failed to fetch") ||
              error.message.includes("Network request failed") ||
              error.message.includes("NetworkError")));

        if (!isNetworkError || !suppressNetworkErrorLogs) {
          logger.error("fetchRaw error: ", { error: error?.message, fetchURL });
        }

        if (isNetworkError) {
          // Surface as status 0 so handleResponse can classify it correctly.
          httpResponseCode = 0;
        } else if (error?.message && error?.message.includes("Received a 401 response")) {
          httpResponseCode = 401;
          if (error?.message.includes("auth/session/refresh")) {
            store.dispatch(logOutUser({}));
          }
        }

        // Call handleResponse exactly once — it calls onReject internally.
        // Do NOT call reject() again here to avoid double-reject.
        handleResponse({
          error,
          httpResponseCode,
          requestMeta: { method, path },
          onFullfill: resolve,
          onReject: reject,
          suppressAuthLogoutOn401: suppressAuthLogoutOn401 || suppressAuthLogoutAfterRefresh401,
          suppressAuthLogoutOn401Codes,
          suppressNoTenantLogoutAndRedirect,
          suppressNetworkErrorLogs
        });
      });
  });
}

function handleResponse({
  responseJson,
  httpResponseCode,
  requestMeta,
  onFullfill = (response) => Promise.resolve(response),
  onReject = (error) => Promise.reject(error),
  suppressAuthLogoutOn401 = false,
  suppressAuthLogoutOn401Codes,
  suppressNoTenantLogoutAndRedirect = false,
  suppressNetworkErrorLogs = false
}: any) {
  const apiCode = responseJson?.content?.code;
  const suppressNoTenantLogout = suppressAuthLogoutOn401 || suppressNoTenantLogoutAndRedirect;
  if (apiCode === "NO_TENANT") {
    const tenant =
      typeof responseJson?.content?.tenant === "string"
        ? responseJson?.content?.tenant
        : typeof responseJson?.content?.tenant_name === "string"
          ? responseJson?.content?.tenant_name
          : "";

    // Best-effort: clear session so the user can re-run sign-up flow.
    if (!suppressNoTenantLogout && typeof window !== "undefined") {
      try {
        store.dispatch(logOutUser({}));
      } catch {
        // ignore
      }

      try {
        const { PATH } = require("@/configs/router/routerConfig");
        const router = require("next/router").default;
        const errorMessage = responseJson?.content?.message || "Tenant not found";
        router.replace({
          pathname: PATH.SIGN_UP,
          query: { tenant, error: errorMessage }
        });
      } catch {
        // ignore navigation failures
      }
    }

    onReject({
      status: httpResponseCode,
      headers: responseJson?.headers ?? {},
      content: responseJson?.content,
      message: responseJson?.content?.message || "",
      localizeMessage: responseJson?.content?.message
    });
    return;
  }

  const localizeMessage = responseJson?.content?.message;
  const headers = responseJson?.headers ?? {};
  const content = responseJson?.content ?? null;
  const suppressAuthLogoutForResponse = shouldSuppressAuthLogout(
    httpResponseCode,
    content,
    suppressAuthLogoutOn401,
    suppressAuthLogoutOn401Codes
  );
  const shouldLogoutOnForbidden = httpResponseCode === 403 && isSessionInvalidatingForbidden(content);
  switch (httpResponseCode) {
    case 200:
    case 201:
    case 202:
    case 204: {
      onFullfill(responseJson);
      break;
    }
    case 401:
      logger.error("FETCHRAW: 401 Unauthorized detected", {
        url: stripQueryFromUrl(responseJson?.url),
        status: httpResponseCode,
        resource:
          requestMeta?.method && requestMeta?.path
            ? `${String(requestMeta.method).toUpperCase()} ${requestMeta.path}`
            : undefined
      });
      onReject({
        status: httpResponseCode,
        headers,
        content,
        message: responseJson?.content?.message || "",
        localizeMessage: localizeMessage
      });
      if (!suppressAuthLogoutForResponse) {
        store.dispatch(logOutUser({}));
      }
      break;
    case 403:
      logger.warn("FETCHRAW: 403 Forbidden detected", {
        url: stripQueryFromUrl(responseJson?.url),
        status: httpResponseCode,
        code: content?.code
      });
      onReject({
        status: httpResponseCode,
        headers,
        content,
        message: responseJson?.content?.message || responseJson?.content?.detail || "",
        localizeMessage
      });
      if (shouldLogoutOnForbidden) {
        store.dispatch(logOutUser({}));
      }
      break;
    case 0: // network unreachable — fetch() threw before any HTTP response
      if (!suppressNetworkErrorLogs) {
        logger.warn("FETCHRAW: Network error", { httpResponseCode, url: responseJson?.url });
      }
      onReject({
        status: httpResponseCode,
        headers,
        content,
        message:
          responseJson?.content?.message ||
          responseJson?.message ||
          "The server is currently unreachable. Please try again later.",
        localizeMessage: "backendUnavailable"
      });
      break;
    case 502: // Bad Gateway
    case 503: // Service Unavailable
    case 504: // Gateway Timeout
      if (!suppressNetworkErrorLogs) {
        logger.warn("FETCHRAW: 5xx Error", { httpResponseCode, url: responseJson?.url });
      }
      onReject({
        status: httpResponseCode,
        headers,
        content,
        message:
          responseJson?.content?.message ||
          responseJson?.message ||
          "The server is currently unreachable. Please try again later.",
        localizeMessage: "backendUnavailable"
      });
      break;
    case 404:
      if (responseJson?.content?.failure_code !== "surface_disabled") {
        logger.warn("FETCHRAW: 400 Bad Request, 404 Not Found, 500 Internal Server Error", {
          httpResponseCode,
          url: responseJson?.url
        });
      }
      onReject({
        status: httpResponseCode,
        headers,
        content,
        message: responseJson?.content?.message || responseJson?.content?.error || "",
        localizeMessage: localizeMessage
      });
      break;
    case 400:
    case 500:
      logger.warn("FETCHRAW: 400 Bad Request, 404 Not Found, 500 Internal Server Error", {
        httpResponseCode,
        url: responseJson?.url
      });
      onReject({
        status: httpResponseCode,
        headers,
        content,
        message: responseJson?.content?.message || responseJson?.content?.error || "",
        localizeMessage: localizeMessage
      });
      break;
    case 406:
      logger.warn("FETCHRAW: 406 Not Acceptable", { httpResponseCode, url: responseJson?.url });
      onReject({
        status: httpResponseCode,
        headers,
        content,
        message: responseJson?.content?.description || "",
        localizeMessage: localizeMessage
      });
      break;
    default: {
      logger.warn("FETCHRAW: unhandled Error", { httpResponseCode, url: responseJson?.url });
      onReject({
        status: httpResponseCode,
        headers,
        content,
        message: responseJson?.content?.message || responseJson?.message || "",
        localizeMessage: localizeMessage
      });
      break;
    }
  }
}

// ---------------------------------------------------------------------------
// fetchRawSafe — non-throwing, typed-result wrapper
// ---------------------------------------------------------------------------

/**
 * Classifies a rejected fetchRaw error object into an `ApiErrorOrigin` string.
 * The mapping mirrors the `handleResponse` switch so callers never need to
 * inspect raw status codes.
 */
function classifyErrorOrigin(status: number, cause: unknown): ApiError["origin"] {
  // Timeout sentinel must be checked first — it also has status 0 but
  // specifically carries the "requestApiTimeout" message.
  if (cause && typeof (cause as any).message === "string" && (cause as any).message === "requestApiTimeout") {
    return "timeout";
  }
  if (status === 0 || status === 502 || status === 503 || status === 504) {
    return "network_down";
  }
  if (status === 401) return "auth";
  if (status === 403 && isSessionInvalidatingForbidden((cause as any)?.content)) return "auth";
  if (status >= 400 && status < 600) return "server";
  return "client";
}

/**
 * A non-throwing, typed wrapper around `FETCHRAW`.
 *
 * Returns `{ ok: true, data: FetchRawResponse }` on success or
 * `{ ok: false, error: ApiError }` on any failure, including:
 *   - network unreachable (origin: "network_down")
 *   - request timeout  (origin: "timeout")
 *   - 401              (origin: "auth")
 *   - 4xx / 5xx        (origin: "server")
 *   - unexpected JS    (origin: "client")
 *
 * Use `import { formatApiError, isBackendUnavailable } from "@/api/apiResult"` to
 * produce user-facing messages without writing switch statements in every component.
 *
 * @example
 *   const result = await fetchRawSafe({ type: "apiurl", path: "/foo/", method: "GET" });
 *   if (!result.ok) {
 *     if (result.error.origin === "network_down") {
 *       setError("The server is unavailable. Please try again later.");
 *     } else {
 *       setError(result.error.message || "An error occurred.");
 *     }
 *     return;
 *   }
 *   setData(result.data.content);
 */
export async function fetchRawSafe(options: FetchRawOptions): Promise<ApiResult<FetchRawResponse>> {
  try {
    const data = await FETCHRAW(options);
    return { ok: true, data };
  } catch (cause: unknown) {
    const rawStatus: number = cause && typeof (cause as any).status === "number" ? (cause as any).status : 0;
    const message: string = cause && typeof (cause as any).message === "string" ? (cause as any).message : "";
    const localizeMessage: string | undefined =
      cause && typeof (cause as any).localizeMessage === "string" ? (cause as any).localizeMessage : undefined;
    const content: unknown =
      cause && typeof (cause as any).content !== "undefined" ? (cause as any).content : undefined;

    const origin = classifyErrorOrigin(rawStatus, cause);

    const error: ApiError = {
      origin,
      status: rawStatus,
      message,
      localizeMessage,
      content,
      cause
    };
    return { ok: false, error };
  }
}
